// Turns an alert's stored reason into what the person reading it can do about it. The keys are
// the exact strings in SKIP_REASON (src/alerts/staff.ts); test/alert-reasons.test.ts holds the two
// together. Everything here is English, translated where it is rendered.

/** `action` names what the dashboard offers next to the line: focus a field, or open a page. */
const FIXES = {
  "No staff phone numbers or emails set": { fix: "Add a staff phone number or email on this page.", action: "phones" },
  "No WhatsApp number connected": { fix: "Connect your WhatsApp business number first.", action: "channels" },
  "Outside the 24h window and no alert template set": {
    fix: "This phone hasn't written to your business number in the last 24 hours, so WhatsApp only accepts an approved template. Set one in Alert template.",
    action: "template",
  },
  "Email alerts aren't configured on this platform": { fix: "Email sending isn't switched on for your account yet. Ask your RELAYZA contact.", action: null },
};

/** Failures whose text comes from Meta or Resend, so only a pattern is stable. */
const PATTERNS = [
  {
    test: /132001|does not exist in/i,
    fix: "Meta doesn't know this template in this language. Check the exact name in Alert template, and the template language on the AI agent page.",
    action: "template",
  },
  { test: /132000/, fix: "The approved template must have exactly one variable, {{1}}.", action: "template" },
  { test: /Resend answered HTTP (401|403)/, fix: "The email sender isn't verified yet. Ask your RELAYZA contact.", action: null },
];

/** { fix, action } for a logged reason, or null when there is nothing specific to suggest. */
export function alertFix(error) {
  if (!error) return null;
  if (FIXES[error]) return FIXES[error];
  return PATTERNS.find((p) => p.test.test(error)) ?? null;
}

export const KNOWN_REASONS = Object.keys(FIXES);

export const KIND_LABEL = {
  handoff: "Needs a person",
  brief: "Daily brief",
  test: "Test",
  budget: "AI budget",
  ai_key: "AI key",
  email: "Email inbox",
  customer_email: "Email to customer",
  missed_call: "Missed call",
};

export const STATUS_LABEL = { sent: "Sent", failed: "Failed", skipped: "Not sent" };

/**
 * The problems behind the latest alert, one line each. Rows arrive newest first; the latest alert
 * is every row logged within a minute of the newest one (one row per recipient), so an old problem
 * that has since been fixed doesn't keep shouting.
 */
export function currentProblems(alerts) {
  const own = (alerts ?? []).filter((a) => a.kind !== "customer_email");
  if (!own.length) return [];
  const newest = Date.parse(own[0].created_at);
  const latest = own.filter((a) => newest - Date.parse(a.created_at) < 60_000);
  const seen = new Set();
  return latest.filter((a) => a.status !== "sent" && a.error && !seen.has(a.error) && seen.add(a.error)).map((a) => a.error);
}
